/*
  Custom-size wallpaper: keeps the product form's quantity equal to the wall area in 0.1 m² units
  (data-units-per-m2) and shows the area, price per m² and total. Markup and pricing rule:
  snippets/wallpaper-size.liquid.

  A4 sample variants (the "send me a sample" switch in the variant picker) are one unit at their own
  price: the size step hides, its inputs are disabled (not required, not sent) and the price line under
  the title shows the sample price instead of the price per m².
*/
if (!customElements.get('wallpaper-size')) {
  customElements.define(
    'wallpaper-size',
    class WallpaperSize extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.unitsPerM2 = Number(this.dataset.unitsPerM2) || 10;
        this.decimals = Math.max(0, Math.round(Math.log10(this.unitsPerM2)));
        this.variants = JSON.parse(this.querySelector('[data-wallpaper-variants]').textContent);
        this.widthInput = this.querySelector('[data-wallpaper-width]');
        this.heightInput = this.querySelector('[data-wallpaper-height]');
        this.areaInput = this.querySelector('[data-wallpaper-area]');
        this.error = this.querySelector('[data-wallpaper-error]');

        this.update = this.update.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        // The variant id input is updated by product-info.js; read it once that has run.
        this.onVariantChange = () => window.requestAnimationFrame(this.update);

        this.widthInput.addEventListener('input', this.update);
        this.heightInput.addEventListener('input', this.update);
        document.addEventListener('variant:changed', this.onVariantChange);
        // Capture phase: runs before the theme's product-form submit handler reads the form data.
        document.addEventListener('submit', this.onSubmit, true);
        this.update();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:changed', this.onVariantChange);
        document.removeEventListener('submit', this.onSubmit, true);
      }

      get quantityInput() {
        return (
          document.querySelector(`input[name="quantity"][form="${this.dataset.formId}"]`) ||
          this.form?.querySelector('input[name="quantity"]')
        );
      }

      // The selected variant: { price (cents, per 1 / unitsPerM2 m² or per sample), sample }.
      get variant() {
        const variant = this.variants[this.form?.querySelector('[name="id"]')?.value];
        return variant || Object.values(this.variants)[0] || { price: 0, sample: false };
      }

      formatMoney(cents) {
        if (window.FoxTheme?.Currency) return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
        return (cents / 100).toFixed(2);
      }

      setValue(selector, value, isHtml = false) {
        const element = this.querySelector(selector);
        if (!element) return;
        if (isHtml) element.innerHTML = value;
        else element.textContent = value;
      }

      // The price line under the title (part "price" of the block).
      setRate(amountHtml, unit) {
        document.querySelectorAll(`[data-wallpaper-rate="${this.dataset.sectionId}"]`).forEach((rate) => {
          const amount = rate.querySelector('.wallpaper-rate__amount');
          const unitElement = rate.querySelector('.wallpaper-rate__unit');
          if (amount) amount.innerHTML = amountHtml;
          if (unitElement) unitElement.textContent = unit;
        });
      }

      setSampleMode(isSample) {
        this.classList.toggle('is-sample', isSample);
        [this.widthInput, this.heightInput, this.areaInput].forEach((input) => (input.disabled = isSample));
      }

      // Returns whether the form can be submitted.
      update() {
        const variant = this.variant;
        const quantityInput = this.quantityInput;

        if (variant.sample) {
          this.setSampleMode(true);
          // A Material Sample Kit (one A4 per material) when the variant picker offers the kit.
          const isKit = !!this.closest('.shopify-section')?.querySelector('[data-sample-kit]');
          this.setRate(this.formatMoney(variant.price), isKit ? 'sample kit' : 'A4 sample');
          if (quantityInput) quantityInput.value = 1;
          this.form?.removeAttribute('data-wallpaper-invalid');
          this.error.hidden = true;
          return true;
        }
        this.setSampleMode(false);

        const width = parseFloat(this.widthInput.value);
        const height = parseFloat(this.heightInput.value);
        const valid = width > 0 && height > 0;
        const rate = this.formatMoney(variant.price * this.unitsPerM2);

        let units = 0;
        let area = 0;
        if (valid) {
          area = (width * height) / 10000;
          // The small epsilon keeps exact sizes (e.g. 6.7 m²) from rounding up a step through float error.
          units = Math.max(1, Math.ceil(area * this.unitsPerM2 - 1e-9));
        }
        const billed = (units / this.unitsPerM2).toFixed(this.decimals);

        this.setValue('[data-wallpaper-wall-area]', valid ? `${area.toFixed(2)} m²` : '—');
        this.setValue('[data-wallpaper-billed]', valid ? `${billed} m²` : '—');
        this.setValue('[data-wallpaper-rate-inline]', rate, true);
        this.setValue('[data-wallpaper-total]', valid ? this.formatMoney(units * variant.price) : '—', true);
        this.setRate(rate, 'per m²');

        this.areaInput.value = valid ? billed : '';
        if (quantityInput && valid) quantityInput.value = units;

        this.form?.toggleAttribute('data-wallpaper-invalid', !valid);
        if (valid) this.error.hidden = true;
        return valid;
      }

      onSubmit(event) {
        if (!this.form || event.target !== this.form) return;
        if (this.update()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.error.hidden = false;
        (this.widthInput.value ? this.heightInput : this.widthInput).focus();
      }
    }
  );
}
